import { createContext, useState, useCallback, useMemo } from 'react'
import Toast from '@components/ui/Toast'

export const ToastContext = createContext()

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random()
    const toast = { id, message, type, duration }

    setToasts((prev) => [...prev, toast])

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }, [removeToast])

  const success = useCallback(
    (message, duration) => addToast(message, 'success', duration),
    [addToast]
  )

  const error = useCallback(
    (message, duration) => addToast(message, 'error', duration),
    [addToast]
  )

  const warning = useCallback(
    (message, duration) => addToast(message, 'warning', duration),
    [addToast]
  )

  const info = useCallback(
    (message, duration) => addToast(message, 'info', duration),
    [addToast]
  )

  const showToast = useCallback((message, type = 'info', duration) => {
    if (type === 'success') return success(message, duration)
    if (type === 'error') return error(message, duration)
    if (type === 'warning') return warning(message, duration)
    return info(message, duration)
  }, [success, error, warning, info])

  // Memoizar o value APENAS com as funções (não inclui toasts)
  // Isso garante que o contexto não muda quando toasts muda, evitando loops
  const value = useMemo(() => ({
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    showToast
  }), [addToast, removeToast, success, error, warning, info, showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  )
}
